import React, { Fragment, useMemo, useRef } from "react";

import { Dialog, Transition } from "@headlessui/react";
import { X } from "phosphor-react";
import PropTypes from "prop-types";

import ModalTitle from "~/admin/components/ModalTitle";

const headerPadding = "px-4 1pt-1 pb-0 sm:p-4 sm:pl-5 sm:pb-0";
const contentPadding = "px-1 pt-2 pb-4 sm:px-4 sm:pt-2 sm:pb-4";
const footerPaddings = "px-1 pb-4 sm:px-4 sm:pb-4";

const Modal = ({
  children,
  visible,
  toggle,
  size = "lg",
  initialFocus = null,
}) => {
  const bodyRef = useRef();

  const widthClass = useMemo(() => {
    switch (size) {
      default:
      case "sm":
        return "sm:w-[500px]";
      case "lg":
        return "sm:w-[600px]";
      case "xl":
        return "sm:w-[1140px]";
    }
  }, [size]);

  return (
    <Transition.Root show={visible} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        onClose={() => {}}
        initialFocus={initialFocus ?? bodyRef}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-[99] inset-0 overflow-y-auto flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <div
                  className={`flex flex-col transform transition-all sm:my-4 max-w-full ${widthClass}`}
                >
                  <div
                    className="absolute self-end text-white cursor-pointer text-[30px] p-4 -mt-0 -mr-0 hover:text-gray-400"
                    onClick={toggle}
                  >
                    <X weight="bold" />
                  </div>

                  <Dialog.Panel>
                    <div
                      ref={bodyRef}
                      className={`bg-slate-800 rounded-md text-left`}
                    >
                      {children}
                    </div>
                  </Dialog.Panel>
                </div>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

Modal.propTypes = {
  children: PropTypes.node.isRequired,
  visible: PropTypes.bool,
  toggle: PropTypes.func,
  size: PropTypes.string,
  initialFocus: PropTypes.any,
  toggleOnOverlay: PropTypes.bool,
  noPadding: PropTypes.bool,
};

const Header = ({ children, className = "" }) => (
  <div
    className={`bg-slate-800 text-white rounded-t-2xl pb-1 border-b  border-slate-500`}
  >
    <div className={headerPadding}>
      <Dialog.Title
        as={ModalTitle}
        className={` ${className}`}
        color="black"
        noMargin
      >
        {children}
      </Dialog.Title>
    </div>
  </div>
);
Modal.Header = Header;

const Body = ({ children, className = "" }) => (
  <div className={`text-gray-300  relative ${contentPadding} ${className}`}>
    {children}
  </div>
);
Modal.Body = Body;

const Footer = ({ children }) => (
  <div
    className={`flex flex-row justify-between items-center ${footerPaddings}`}
  >
    {children}
  </div>
);
Modal.Footer = Footer;

export default Modal;
